import { useState, type ReactNode } from "react";
import { consumeReferralCode } from "../lib/referral";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useVenue } from "../lib/venue";
import { isBookingVenue, bookingWords } from "../lib/venue-type";
import { PageSkeleton } from "../components/skeleton";
import { SubscriptionDisclosureModal } from "../components/subscription-disclosure-modal";
import { useAuth } from "../lib/auth";
import { isNativeApp } from "../lib/platform";
import {
  createSubscribeCheckout,
  getMySubscription,
  getVenueEventsWithClosing,
  getMyBookings,
  getVenueBenefits,
  getVenueBulletins,
} from "../lib/api";
import type { Tier, Benefit } from "../lib/types";
import { isEventLive } from "../lib/events";
import { LiveEventStrip } from "../components/live-event-strip";
import { orderedVisibleSections, isSectionHidden } from "../lib/sections";
import {
  shouldShowStatsBlock,
  shouldShowLatestNews,
  shouldShowComingSoon,
  pickThisWeekEvents,
  pickHomeRedeemables,
  isRedemptionEligible,
} from "../lib/venue-home-decisions";
import { BenefitRedeemCard } from "../components/benefit-redeem-card";
import { normaliseEventsResponse } from "../lib/normalise-events-response";
import { overlayColors, overlayScrimHorizontal, overlayScrimVertical } from "../lib/image-overlay";
import { StampGlyph, isStampIcon } from "../lib/loyalty-icons";
import { RecordsStorefront } from "./records";

export function VenuePage() {
  const { data, isLoading, error, host } = useVenue();
  const { user } = useAuth();

  const venueId = data?.venue.id;
  const isStudio = isBookingVenue(data?.venue.archetype);
  const words = bookingWords(data?.venue.archetype);

  // Subscription drives the member badge + access-gated UI, mirroring
  // getUserVenueSubscription in the Next.js venue page.
  const subQuery = useQuery({
    queryKey: ["my-subscription", venueId, user?.id ?? null],
    queryFn: () => getMySubscription(venueId!),
    enabled: !!venueId && !!user,
    retry: false,
  });

  // Public benefit list, so non-members see what they get before the paywall.
  const benefitsQuery = useQuery({
    queryKey: ["venue-benefits", venueId],
    queryFn: () => getVenueBenefits(venueId!),
    enabled: !!venueId,
  });

  // Hospitality venues show an upcoming-events carousel + an "on now" hero
  // above it when a currently-live event exists (Love Inn / Prospect case:
  // Friday-night gig runs until 4am Sat and stays surfaced past midnight).
  //
  // MUST use a distinct queryKey from EventsPage: that page fetches the
  // unwrapped `EventSummary[]` under `["events", venueId]`, so if we shared
  // that key, react-query would hand us the array cached by a prior
  // /events visit and `eventsData.events` would be undefined, then
  // `.find()` throws (Love Inn prod incident 2026-09-07, err was
  // "undefined is not an object (evaluating 'pe.find')").
  const eventsQuery = useQuery({
    queryKey: ["events-with-closing", venueId],
    queryFn: () => getVenueEventsWithClosing(venueId!),
    enabled: !!venueId && !isStudio,
  });

  // Home real-estate additions (Love Inn ask 2026-09-07): stats block,
  // latest news card, this-week vertical events list. Hospitality only,
  // studio/wellness/etc use their own layout.
  const bulletinsQuery = useQuery({
    queryKey: ["bulletins", venueId],
    queryFn: () => getVenueBulletins(venueId!),
    enabled: !!venueId && !isStudio,
    staleTime: 5 * 60_000,
  });
  // Studio venues with access show the member's upcoming bookings.
  const bookingsQuery = useQuery({
    queryKey: ["my-bookings", venueId, user?.id ?? null],
    queryFn: () => getMyBookings(venueId!),
    enabled: !!venueId && !!user && !!isStudio,
  });

  if (isLoading) {
    return <PageSkeleton rows={5} />;
  }

  if (error || !data) {
    return (
      <div className="state error">
        <h1>Venue not found</h1>
        <div className="detail">
          <code>{host}</code>
          <br />
          {String(error?.message ?? "unknown error")}
        </div>
      </div>
    );
  }

  const { venue, tiers } = data;

  // Apple guideline 2.1(b)/3.1.1: the native apps must not present a purchase
  // surface for a venue that isn't taking payments yet (an un-onboarded Stripe
  // account makes checkout fail, which reads as a broken in-app purchase in
  // review). Hide the membership paywall on native until Stripe is live; the
  // web storefront is unaffected, and it re-appears automatically once the
  // venue onboards.
  const hideMembershipOnNative = isNativeApp() && !venue.stripeOnboardingComplete;

  // Record shops get a catalogue storefront as their home instead of the
  // membership landing (see RecordsStorefront). Membership/loyalty still
  // reachable via their own routes.
  if (venue.archetype === "RECORD_SHOP") {
    return <RecordsStorefront />;
  }

  // Admin-controlled section visibility + order (Branding & App Design).
  const orderedCards = orderedVisibleSections(venue.archetype, venue.appConfig, {
    loyaltyEnabled: venue.loyaltyEnabled,
    hasEvents: venue.hasPublishedEvents,
  });
  const hidden = (key: string) => isSectionHidden(venue.appConfig, key);

  function featureCard(key: string) {
    const bgImg = venue.appConfig?.sectionImages?.[key];
    const t = (def: string) => venue.appConfig?.sectionTitles?.[key] || def;
    // Over an image, text must contrast the photo (admin-controlled), NOT the
    // theme, else a light theme paints dark text over a dark photo.
    const ov = overlayColors(venue.appConfig?.imageOverlay);
    // Per-section card layout (Phase 2b). Default "row" reproduces today.
    const layout = venue.appConfig?.sectionLayout?.[key] ?? "row";
    const wrap = (to: string, icon: ReactNode, title: string, sub: string) => {
      // Minimal, text-only link row, no icon or image.
      if (layout === "minimal") {
        return (
          <Link
            key={key}
            to={to}
            className="flex items-center justify-between gap-3 rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] px-4 py-3 transition hover:bg-[var(--surface-2)]"
          >
            <span className="font-medium text-[var(--foreground)]">{title}</span>
            <span aria-hidden className="text-lg leading-none text-[var(--muted)]">›</span>
          </Link>
        );
      }
      // Feature, tall image card with title overlaid (accent tint if no image).
      if (layout === "feature") {
        return (
          <Link
            key={key}
            to={to}
            className="relative flex min-h-[150px] flex-col justify-end overflow-hidden rounded-[var(--radius)] border border-[var(--border-default)] p-4 transition hover:border-[var(--border-strong)]"
          >
            {bgImg ? (
              <>
                <img src={bgImg} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <span className="absolute inset-0" style={{ background: overlayScrimVertical(ov) }} />
              </>
            ) : (
              <span className="absolute inset-0 bg-[var(--accent)]/15" />
            )}
            <div className="relative">
              <h3 className="text-lg font-bold text-[var(--foreground)]" style={bgImg ? { color: ov.fg } : undefined}>{title}</h3>
              <p className="text-sm text-[var(--subtle)]" style={bgImg ? { color: ov.sub } : undefined}>{sub}</p>
            </div>
          </Link>
        );
      }
      // Row (default), compact icon + title + subtitle.
      return (
        <Link
          key={key}
          to={to}
          className={`relative flex items-center gap-3 overflow-hidden rounded-[var(--radius)] border border-[var(--border-default)] p-4 transition hover:border-[var(--border-strong)] ${bgImg ? "" : "bg-[var(--surface)] hover:bg-[var(--surface-2)]"}`}
        >
          {bgImg && <img src={bgImg} alt="" className="absolute inset-0 h-full w-full object-cover" />}
          {bgImg && <span className="absolute inset-0" style={{ background: overlayScrimHorizontal(ov) }} />}
          <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]">
            {icon}
          </span>
          <div className="relative">
            <h3 className="font-semibold text-[var(--foreground)]" style={bgImg ? { color: ov.fg } : undefined}>{title}</h3>
            <p className="text-sm text-[var(--subtle)]" style={bgImg ? { color: ov.sub } : undefined}>{sub}</p>
          </div>
        </Link>
      );
    };
    const ic = (d: string, extra?: string) => (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 text-[var(--accent-fg)]">
        {extra ? <path d={extra} /> : null}
        <path fillRule="evenodd" d={d} clipRule="evenodd" />
      </svg>
    );
    switch (key) {
      case "noticeboard":
        return wrap("/noticeboard", ic("M4.125 3C3.089 3 2.25 3.84 2.25 4.875V18a3 3 0 0 0 3 3h15a3 3 0 0 1-3-3V4.875C17.25 3.839 16.41 3 15.375 3H4.125ZM12 9.75a.75.75 0 0 0 0 1.5h1.5a.75.75 0 0 0 0-1.5H12Zm-.75-2.25a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5H12a.75.75 0 0 1-.75-.75ZM6 12.75a.75.75 0 0 0 0 1.5h7.5a.75.75 0 0 0 0-1.5H6Zm-.75 3.75a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5H6a.75.75 0 0 1-.75-.75ZM6 6.75a.75.75 0 0 0-.75.75v3c0 .414.336.75.75.75h3a.75.75 0 0 0 .75-.75v-3A.75.75 0 0 0 9 6.75H6Z", "M18.75 6.75h1.875c.621 0 1.125.504 1.125 1.125V18a1.5 1.5 0 0 1-3 0V6.75Z"), t("Noticeboard"), "Announcements and news");
      case "benefits":
        return wrap("/benefits", ic("M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"), t("Member Benefits"), isStudio ? `${words.Noun} access and perks` : "Discounts, priority booking, and more");
      case "loyalty": {
        // Opt-in stamp card: when the admin pinned a reward (sensible stamp
        // count), show a progress grid instead of the plain link row. Filled
        // stamps = the member's point balance toward that reward.
        const sc = venue.stampCard;
        if (sc && sc.target >= 1 && sc.target <= 12) {
          const lcfg = venue.appConfig?.loyalty;
          const iconRaw = lcfg?.stampIcon;
          const icon = isStampIcon(iconRaw) ? iconRaw : "star";
          const unit = lcfg?.unitLabel?.trim();
          const balance = subscription?.pointBalance ?? 0;
          const filled = Math.max(0, Math.min(balance, sc.target));
          return (
            <Link
              key={key}
              to="/rewards"
              className="block rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-4 transition hover:bg-[var(--surface-2)]"
            >
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold text-[var(--foreground)]">{t("Loyalty")}</h3>
                <span className="text-sm font-medium text-[var(--muted)]">
                  {filled}/{sc.target}{unit ? ` ${unit}` : ""}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {Array.from({ length: sc.target }).map((_, i) => {
                  const on = i < filled;
                  return (
                    <span
                      key={i}
                      className={`flex aspect-square items-center justify-center rounded-full ${
                        on
                          ? "bg-[var(--accent)] text-[var(--accent-fg)]"
                          : "border border-dashed border-[var(--border-strong)] text-[var(--muted)]"
                      }`}
                    >
                      {on ? (
                        <StampGlyph icon={icon} className="h-5 w-5" />
                      ) : (
                        <span className="text-lg leading-none">+</span>
                      )}
                    </span>
                  );
                })}
              </div>
            </Link>
          );
        }
        return wrap("/rewards", ic("M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"), t(venue.loyaltyProgramName || "Loyalty"), "Earn points and rewards");
      }
      case "events":
        return wrap("/events", ic("M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z"), t("Events"), "First access and member pricing");
      default:
        return null; // e.g. "sessions" → studio Book-a-Desk card is rendered separately
    }
  }
  const subscription = subQuery.data ?? null;
  const now = new Date();

  // User has access if active OR if cancelled but period hasn't ended yet.
  const hasActiveAccess =
    subscription?.status === "ACTIVE" ||
    (subscription?.status === "CANCELED" &&
      !!subscription.currentPeriodEnd &&
      new Date(subscription.currentPeriodEnd) > now);
  const isCancelled = subscription?.status === "CANCELED";

  // Belt-and-braces on top of the distinct queryKey: normalise into
  // { events, closingTimes } no matter what react-query hands us. The
  // Love Inn incident above proves how expensive a single unguarded
  // `.find()` on undefined is at prod.
  const { events: rawEvents, closingTimes } = normaliseEventsResponse(
    eventsQuery.data,
  );
  const liveEvent = rawEvents.find((e) => isEventLive(e, closingTimes, now));
  // Don't dupe the live event in the "upcoming" carousel, it's the hero above.
  const events = liveEvent ? rawEvents.filter((e) => e.id !== liveEvent.id) : rawEvents;

  // "This week" = events starting in the next 7 days, falling back to the
  // first 4 upcoming when the week is empty so a quiet week doesn't blank
  // the section (Issy's Love Inn home rework 2026-09-07). Pure logic +
  // regression tests live in web/src/lib/venue-home-decisions.ts.
  const { list: thisWeekEvents, hasMore: hasMoreEvents } = pickThisWeekEvents({
    events,
    now,
  });

  // Latest news = top pinned bulletin, else newest. The public bulletins
  // endpoint already orders `[{ pinned: desc }, { createdAt: desc }]` so
  // the first item is always the right one.
  const latestBulletin = bulletinsQuery.data?.[0] ?? null;
  // Still gates the "Ready to claim" redeemables section below, the
  // points/upcoming-tickets stats cards that used to share this gate were
  // removed (Issy 2026-09-09).
  const statsBlockVisible = shouldShowStatsBlock({
    hasActiveAccess,
    isStudio,
    hasUser: !!user,
  });
  const latestNewsVisible = shouldShowLatestNews({
    isStudio,
    hasBulletin: latestBulletin != null,
  });
  const comingSoonConfig = (venue.appConfig as { homeComingSoon?: { enabled?: boolean; title?: string; body?: string } } | null | undefined)?.homeComingSoon ?? null;
  const comingSoonVisible = shouldShowComingSoon({ isStudio, config: comingSoonConfig });

  // Home redeemables: every redeemable benefit on the member's tier or
  // below, rendered inline so tapping Redeem happens on the home surface
  // (Fenton 2026-09-09, "no click-through to /benefits"). Pure ordering
  // + gate live in venue-home-decisions.ts. MySubscription.tier doesn't
  // carry sortOrder so we resolve it via the venue tiers list.
  const memberTier = subscription?.tier
    ? tiers.find((t) => t.id === subscription.tier.id) ?? null
    : null;
  // Deliberately isRedemptionEligible (ACTIVE only), NOT hasActiveAccess.
  // A CANCELED-in-period sub can still USE the venue but can't redeem, so
  // rendering redeem buttons for them would just 403. Peer review 2026-09-09.
  const homeRedeemables = pickHomeRedeemables({
    benefits: benefitsQuery.data ?? [],
    memberTierSortOrder:
      isRedemptionEligible(subscription?.status) && memberTier ? memberTier.sortOrder : null,
  });
  const userBookings = (bookingsQuery.data ?? []).filter(
    (b) => new Date(b.date) >= new Date(now.toDateString()),
  );

  const defaultWelcome = isStudio
    ? `${words.cta} at ${venue.appName || venue.name}. Members get priority booking and perks.`
    : `Exclusive benefits, merch, tickets and more for ${venue.appName || venue.name} subscribers.`;

  const venueDisplayName = venue.appName || venue.name;

  // In Assembly-managed website mode the VenueWebLayout hero already shows the
  // name + welcome, so suppress this page's own header to avoid duplicating it.
  const websiteMode = venue.appConfig?.websiteMode === true;

  // Opt-in heading treatment (Design app → Branding). Absent = today's default.
  const hc = venue.appConfig?.heading;
  // Numeric sizePx/weightValue (when set) override the preset size/weight via
  // inline style, so we drop the matching Tailwind class to avoid a conflict.
  const headingClass = [
    hc?.sizePx != null ? "" : hc?.size === "md" ? "text-xl" : hc?.size === "lg" ? "text-3xl" : hc?.size === "xl" ? "text-4xl" : "text-2xl",
    hc?.weightValue != null ? "" : hc?.weight === "normal" ? "font-normal" : hc?.weight === "black" ? "font-black" : "font-bold",
    hc?.uppercase ? "uppercase tracking-tight leading-tight" : "",
    "text-[var(--foreground)]",
  ].filter(Boolean).join(" ");
  const headingStyle = {
    ...(hc?.sizePx != null ? { fontSize: `${hc.sizePx}px`, lineHeight: 1.05 } : {}),
    ...(hc?.weightValue != null ? { fontWeight: hc.weightValue } : {}),
  };

  return (
    <div className="space-y-6">
      {/* Header, suppressed in website mode (the web hero shows name + welcome). */}
      {!websiteMode && (
        <section className="-mx-4 -mt-6 px-4 pt-4">
          <h1 className={headingClass} style={headingStyle}>{venueDisplayName}</h1>
          <p className="mt-2 mb-2 text-sm leading-relaxed text-[var(--subtle)]">
            {venue.appWelcomeText || defaultWelcome}
          </p>
        </section>
      )}

      {/* Member badge */}
      {hasActiveAccess && subscription && (
        <div className="-mt-4 flex items-center gap-3 rounded-[var(--radius)] bg-[var(--accent)]/20 p-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--accent)]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="h-5 w-5 text-[var(--accent-fg)]"
            >
              <path
                fillRule="evenodd"
                d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <p className="font-semibold text-[var(--foreground)]">Member of {venueDisplayName}</p>
            {isCancelled && subscription.currentPeriodEnd ? (
              <p className="text-sm text-yellow-400">
                Access until{" "}
                {new Date(subscription.currentPeriodEnd).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            ) : (
              <p className="text-sm text-[var(--subtle)]">Your benefits are active</p>
            )}
          </div>
        </div>
      )}

      {/* Membership CTA - For non-members */}
      {!hasActiveAccess && tiers[0] && !hideMembershipOnNative && (
        <section className="-mt-4">
          <MembershipCta
            tier={tiers[0]}
            totalTiers={tiers.length}
            venueId={venue.id}
            stripeReady={venue.stripeOnboardingComplete}
            isLoggedIn={!!user}
            benefits={(benefitsQuery.data ?? []).filter(
              (b) => b.tier.sortOrder <= tiers[0].sortOrder,
            )}
          />
        </section>
      )}

      {/* "Coming soon" teaser, opt-in per venue via appConfig.homeComingSoon
          (Issy 2026-09-09 for Love Inn; cross-tenant fix 2026-09-09 so
          Prospect/Toka/etc. don't inherit Love Inn's roadmap copy). Title +
          body are configurable; the defaults are the Love Inn wording. */}
      {comingSoonVisible && (
        <section className="flex items-start gap-3 rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-white">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.898 20.572 16.5 21.75l-.398-1.178a3.375 3.375 0 0 0-2.174-2.174L12.75 18l1.178-.398a3.375 3.375 0 0 0 2.174-2.174L16.5 14.25l.398 1.178a3.375 3.375 0 0 0 2.174 2.174L20.25 18l-1.178.398a3.375 3.375 0 0 0-2.174 2.174Z" />
            </svg>
          </span>
          <div>
            <p className="font-semibold text-[var(--foreground)]">
              {comingSoonConfig?.title || "Coming soon"}
            </p>
            <p className="mt-1 text-sm font-normal text-white/80">
              {comingSoonConfig?.body ||
                "Stay tuned for our new features incl. loyalty scheme, exclusive access and member perks."}
            </p>
          </div>
        </section>
      )}

      {/* Redeemables: full inline redeem cards on home, no click-through
          to /benefits (Fenton 2026-09-09). Welcome perks first, then
          monthly, then other redeemables (see pickHomeRedeemables). Each
          card runs its own state machine (idle -> redeemed proof +
          countdown, or locked + countdown), matching the RedeemCard
          visual on nfc-tap.tsx. */}
      {statsBlockVisible && homeRedeemables.length > 0 && (
        <section className="flex flex-col gap-2">
          {homeRedeemables.map((b) => (
            <BenefitRedeemCard
              key={b.id}
              venueId={venue.id}
              variant="hero"
              benefit={b}
              label={b.type}
            />
          ))}
        </section>
      )}

      {/* Latest news card: newest pinned bulletin (or the newest overall).
          Tap-through to /noticeboard for the full list. Only render when
          there's actually a bulletin to show. */}
      {latestNewsVisible && latestBulletin && (
        <section>
          <Link
            to="/noticeboard"
            className="block rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-4 transition hover:bg-[var(--surface-2)]"
          >
            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--accent)]">
              {latestBulletin.pinned && <span aria-hidden>📌</span>}
              Latest news
            </div>
            <p className="font-semibold text-[var(--foreground)] line-clamp-1">{latestBulletin.title}</p>
            <p className="mt-1 text-sm text-[var(--muted)] line-clamp-2">{latestBulletin.body}</p>
          </Link>
        </section>
      )}

      {/* Studio: Book a Desk CTA (primary action for studio venues) */}
      {isStudio && hasActiveAccess && (
        <section className="-mt-2">
          <Link
            to="/book"
            className="block rounded-[var(--radius)] border border-[var(--accent)] bg-[var(--accent)]/10 p-4 transition hover:bg-[var(--accent)]/20"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-5 w-5 text-[var(--accent-fg)]"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
                <div>
                  <h3 className="font-semibold text-[var(--foreground)]">{words.cta}</h3>
                  <p className="text-sm text-[var(--subtle)]">{words.subtitle}</p>
                </div>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-5 w-5 text-[var(--accent)]"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            </div>
          </Link>
        </section>
      )}

      {/* Studio: Upcoming bookings */}
      {isStudio && hasActiveAccess && userBookings.length > 0 && (
        <section className="rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-4">
          <h3 className="text-sm font-medium text-[var(--subtle)]">Your Upcoming Bookings</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {userBookings.slice(0, 4).map((booking) => (
              <span
                key={booking.id}
                className="inline-flex items-center rounded-full bg-[var(--accent)]/20 px-3 py-1 text-sm text-[var(--foreground)]"
              >
                {new Date(booking.date).toLocaleDateString("en-GB", {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}
              </span>
            ))}
            {userBookings.length > 4 && (
              <Link
                to="/account"
                className="inline-flex items-center rounded-full bg-[var(--surface-2)] px-3 py-1 text-sm text-[var(--subtle)]"
              >
                +{userBookings.length - 4} more
              </Link>
            )}
          </div>
        </section>
      )}

      {/* Feature Cards, APP navigation (Noticeboard / Benefits / Events tiles).
          Suppressed in website mode: the website has its own header/hero/footer
          navigation, so these app tiles would read as clutter on a web page. */}
      {!websiteMode && <section className="space-y-2">
        {/* Book a Desk Card - for non-members in studio */}
        {isStudio && !hasActiveAccess && (
          <Link
            to="/book"
            className={`flex items-center gap-3 rounded-[var(--radius)] border p-4 transition hover:border-[var(--border-strong)] hover:bg-[var(--surface-2)] ${
              venue.studioTrialPassEnabled
                ? "border-[var(--accent)]/30 bg-[var(--accent)]/10"
                : "border-[var(--border-default)] bg-[var(--surface)]"
            }`}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="h-4 w-4 text-[var(--accent-fg)]"
              >
                <path
                  fillRule="evenodd"
                  d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            <div>
              {venue.studioTrialPassEnabled ? (
                <>
                  <h3 className="font-semibold text-[var(--foreground)]">Try a Free Day</h3>
                  <p className="text-sm text-[var(--subtle)]">Book your free trial session</p>
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-[var(--foreground)]">{words.cta}</h3>
                  {/* STUDIO (desk booking) gates on active membership; WELLNESS
                      (sauna sessions) is pay-per-visit, guest checkout works
                      without an account. Don't tell WELLNESS visitors they
                      need a membership they don't. */}
                  <p className="text-sm text-[var(--subtle)]">
                    {venue.archetype === "WELLNESS" ? "Pay-per-session" : "Membership required"}
                  </p>
                </>
              )}
            </div>
          </Link>
        )}

        {/* Feature cards, rendered in the admin-configured order/visibility
            (Branding & App Design). See featureCard() above. */}
        {orderedCards.map((k) => featureCard(k))}
      </section>}

      {/* "On Now" strip: shown above the upcoming-events carousel when an
          event is currently live (event.date <= now < resolvedEnd). Resolved
          end uses event.endDate when set, else the venue's per-day
          closingTimes config, else a 4h fallback. Bristol/Love Inn fix. */}
      {!isStudio && !hidden("events") && liveEvent && (
        <section>
          <LiveEventStrip
            event={liveEvent}
            closingTimes={closingTimes}
            variant="app"
            venueLogoUrl={venue.appIconUrl || venue.imageUrl}
            venueName={venue.name}
          />
        </section>
      )}

      {/* "This week" events, vertical stack, replaces the horizontal carousel
          of all events (Issy's Love Inn home rework 2026-09-07). Same click
          target, richer layout: bigger image, artist + venue subtitle. When
          there ARE more events beyond this week, a "View all" link goes to
          the full list. Website mode: render only when events exist. */}
      {!isStudio && !hidden("events") && !(websiteMode && events.length === 0) && (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-semibold text-[var(--foreground)]">
              {thisWeekEvents.length > 0 ? "This week" : "Upcoming events"}
            </h2>
            {hasMoreEvents && (
              <Link to="/events" className="text-xs text-[var(--accent)]">
                View all
              </Link>
            )}
          </div>

          {events.length === 0 ? (
            <div className="rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-6 text-center">
              <p className="text-sm text-[var(--muted)]">No upcoming events</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {(thisWeekEvents.length > 0 ? thisWeekEvents : events.slice(0, 4)).map((event) => (
                <Link
                  key={event.id}
                  to="/events/$id"
                  params={{ id: event.id }}
                  className="flex items-stretch gap-3 rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] overflow-hidden transition hover:border-[var(--border-strong)] hover:bg-[var(--surface-2)]"
                >
                  <EventCardImage
                    eventImageUrl={event.imageUrl}
                    venueLogoUrl={venue.appIconUrl || venue.imageUrl}
                    venueName={venue.name}
                    className="h-20 w-20 shrink-0"
                  />
                  <div className="flex min-w-0 flex-1 flex-col justify-center pr-3 py-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--accent)]">
                      {new Date(event.date).toLocaleDateString("en-GB", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </p>
                    <p className="mt-0.5 truncate text-sm font-semibold text-[var(--foreground)]">
                      {event.title}
                    </p>
                    {event.artist && (
                      <p className="truncate text-xs text-[var(--muted)]">{event.artist}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// Membership CTA card for non-members, matches the bordered tier panel in
// src/app/venue/page.tsx. Keeps the SPA's existing subscribe-checkout flow
// (createSubscribeCheckout + SubscriptionDisclosureModal).
function benefitLabel(b: Benefit): string {
  if (b.description) return b.description;
  const t = b.type.replace(/_/g, " ").toLowerCase();
  return b.value ? `${t}: ${b.value}` : t.charAt(0).toUpperCase() + t.slice(1);
}

function MembershipCta({
  tier,
  totalTiers,
  venueId,
  stripeReady,
  isLoggedIn,
  benefits,
}: {
  tier: Tier;
  totalTiers: number;
  venueId: string;
  stripeReady: boolean;
  isLoggedIn: boolean;
  benefits: Benefit[];
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirmSubscribe() {
    setLoading(true);
    setError(null);
    try {
      const { url } = await createSubscribeCheckout(tier.id, "/", consumeReferralCode());
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setLoading(false);
      setModalOpen(false);
    }
  }

  const priceNumber = Number(tier.price);
  const currencySymbol = tier.currency === "GBP" ? "£" : tier.currency;

  return (
    <div className="rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-4">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-[var(--foreground)]">{tier.name}</p>
        <p className="font-semibold text-[var(--foreground)]">
          {currencySymbol}
          {priceNumber.toFixed(0)}
          <span className="text-sm font-normal text-[var(--muted)]">
            /{tier.interval === "MONTHLY" ? "month" : "year"}
          </span>
        </p>
      </div>
      {tier.minCommitmentMonths && (
        <p className="mt-1 text-right text-xs text-[var(--subtle)]">
          Min {tier.minCommitmentMonths}-month commitment
        </p>
      )}

      {/* What you get, show value before asking for payment */}
      {benefits.length > 0 && (
        <ul className="mt-4 space-y-2 border-t border-[var(--border-default)] pt-4">
          {benefits.slice(0, 6).map((b) => (
            <li key={b.id} className="flex items-start gap-2 text-sm text-[var(--foreground)]/85">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="mt-0.5 h-4 w-4 shrink-0 text-[var(--accent)]">
                <path fillRule="evenodd" d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd" />
              </svg>
              <span>{benefitLabel(b)}</span>
            </li>
          ))}
          {benefits.length > 6 && (
            <li className="text-xs text-[var(--muted)]">+{benefits.length - 6} more benefits</li>
          )}
        </ul>
      )}

      <div className="mt-4">
        {!stripeReady ? (
          <p className="text-sm text-[var(--subtle)]">
            This venue isn&apos;t taking payments yet, check back soon.
          </p>
        ) : isLoggedIn ? (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            disabled={loading}
            className="w-full rounded-[var(--radius)] bg-[var(--accent)] px-4 py-3 text-sm font-bold transition hover:opacity-90 disabled:opacity-50"
            style={{ color: "var(--accent-fg)" }}
          >
            {loading ? "Loading..." : "Subscribe"}
          </button>
        ) : (
          <Link
            to="/sign-in"
            className="block w-full rounded-[var(--radius)] bg-[var(--accent)] px-4 py-3 text-center text-sm font-bold transition hover:opacity-90"
            style={{ color: "var(--accent-fg)" }}
          >
            Sign in to subscribe
          </Link>
        )}
        {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
      </div>

      {totalTiers > 1 && (
        <div className="mt-3 text-center">
          <Link
            to="/tiers"
            className="text-xs uppercase tracking-[0.2em] text-[var(--subtle)] underline-offset-4 hover:text-[var(--foreground)] hover:underline"
          >
            See all {totalTiers} tiers
          </Link>
        </div>
      )}

      <SubscriptionDisclosureModal
        tier={tier}
        venueId={venueId}
        isOpen={modalOpen}
        loading={loading}
        onClose={() => setModalOpen(false)}
        onConfirm={confirmSubscribe}
      />
    </div>
  );
}

// Inline port of src/app/venue/event-card-image.tsx, fallback chain:
// event image → venue logo → calendar glyph.
type Stage = "event" | "logo" | "icon";

function EventCardImage({
  eventImageUrl,
  venueLogoUrl,
  venueName,
  className,
}: {
  eventImageUrl: string | null;
  venueLogoUrl: string | null;
  venueName: string;
  className: string;
}) {
  const initial: Stage = eventImageUrl ? "event" : venueLogoUrl ? "logo" : "icon";
  const [stage, setStage] = useState<Stage>(initial);

  if (stage === "event" && eventImageUrl) {
    return (
      <img
        src={eventImageUrl}
        alt=""
        className={`${className} object-cover`}
        onError={() => setStage(venueLogoUrl ? "logo" : "icon")}
      />
    );
  }

  if (stage !== "icon" && venueLogoUrl) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-[var(--accent)]/10 p-4`}
      >
        <img
          src={venueLogoUrl}
          alt={venueName}
          className="max-h-full max-w-full object-contain opacity-80"
          onError={() => setStage("icon")}
        />
      </div>
    );
  }

  return (
    <div className={`${className} flex items-center justify-center bg-[var(--surface-2)]`}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="h-8 w-8 text-[var(--muted)]"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
        />
      </svg>
    </div>
  );
}
